export const SOLO_LECTURA_ROL_ID = 4;
export const ADMIN_ROL_ID = 1;
// Admin Regional: ve todos los departamentos, pero solo de su(s) hotel(es)
// asignado(s) (el hotel ya se restringe con tbaUsuariosHoteles, igual que a
// cualquier usuario). Admin de Departamento: acotado a su(s) departamento(s),
// pero con acceso administrativo (usuarios, flujos de firmas) dentro de eso.
export const ADMIN_REGIONAL_ROL_ID = 6;
export const ADMIN_DEPARTAMENTO_ROL_ID = 7;

export function getCurrentUser(): any {
  try {
    return JSON.parse(sessionStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

export function isSoloLectura(): boolean {
  return getCurrentUser()?.RolId === SOLO_LECTURA_ROL_ID;
}

// Administrador global únicamente (acceso total, sin ninguna acotación). Se
// usa para las pocas cosas que solo el admin global puede hacer, como crear
// otro admin (de cualquier tipo).
export function isAdmin(): boolean {
  return getCurrentUser()?.RolId === ADMIN_ROL_ID;
}

export function esAdminRegional(): boolean {
  return getCurrentUser()?.RolId === ADMIN_REGIONAL_ROL_ID;
}

export function esAdminDepartamento(): boolean {
  return getCurrentUser()?.RolId === ADMIN_DEPARTAMENTO_ROL_ID;
}

// Cualquiera de los tres niveles de administrador (global, regional, de
// departamento). Los tres tienen acceso administrativo "de por sí" a todos
// los módulos y pueden entrar a Configuración > Usuarios; lo que cambia entre
// ellos es el ALCANCE de los datos (hotel/departamento), no qué páginas ven.
export function esAdminDeAlgunTipo(): boolean {
  const rol = getCurrentUser()?.RolId;
  return rol === ADMIN_ROL_ID || rol === ADMIN_REGIONAL_ROL_ID || rol === ADMIN_DEPARTAMENTO_ROL_ID;
}

// Quién puede entrar a Configuración > Usuarios y crear/gestionar usuarios.
export function puedeAdministrarUsuarios(): boolean {
  return esAdminDeAlgunTipo();
}

// Departamentos que el usuario puede ver: null significa "todos" (rol con
// VeTodosDepartamentos), un arreglo significa "solo estos". Si el usuario no
// tiene un scope multi-departamento asignado explícitamente (DepartamentoIds),
// se cae al comportamiento de siempre: solo su propio DepartamentoId.
export function getDepartamentosPermitidos(): number[] | null {
  const user = getCurrentUser();
  if (!user || user.VeTodosDepartamentos) return null;

  const raw = user.DepartamentoIds;
  if (raw) {
    const ids = String(raw)
      .split(',')
      .map((s: string) => Number(s.trim()))
      .filter((n: number) => !isNaN(n));
    if (ids.length > 0) return ids;
  }
  return user.DepartamentoId != null ? [Number(user.DepartamentoId)] : null;
}

// true si el usuario puede acceder al módulo (por clave). Los tres niveles de
// administrador (global, regional, de departamento) siempre ven todos los
// módulos — el sistema de módulos por usuario es para acotar al personal
// regular, no a los admins; lo que acota a un admin regional/departamento es
// el ALCANCE de los datos (su hotel/departamento), no las páginas que ve.
// Para el resto: si no tiene ningún módulo asignado explícitamente todavía,
// sin restricción (ve todo lo que su rol ya le permita). En cuanto tiene al
// menos un módulo asignado, su acceso pasa a ser exactamente ese conjunto.
export function tieneModulo(clave: string): boolean {
  if (esAdminDeAlgunTipo()) return true;

  const user = getCurrentUser();
  const raw = user?.Modulos;
  if (!raw) return true;
  const claves = String(raw)
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean);
  return claves.includes(clave);
}

// A qué ruta (layout+path) lleva cada módulo, para poder redirigir sin
// depender de importar routes.tsx completo (que arrastra todos los
// componentes de vista). Debe reflejar los mismos moduloClave de routes.tsx.
const RUTA_POR_MODULO: Record<string, string> = {
  dashboard: '/admin/dashboards',
  activos: '/admin/main/ecommerce/order-list',
  resguardos: '/admin/main/ecommerce/referrals',
  importar_activos: '/admin/main/importar/activos',
  salidas: '/admin/main/ver/salidas',
  historial_bajas: '/admin/main/historial-bajas',
  facturas: '/admin/main/facturas',
  config_flujos_firmas: '/admin/configuracion-salidas/tipos-baja',
  config_usuarios: '/admin/configuracion/usuarios',
  config_depreciacion: '/admin/configuracion/depreciacion',
  config_agentes_seguridad: '/admin/configuracion/agentes-seguridad',
};

// Busca, en orden de prioridad (columna Orden de tbaModulos), el primer
// módulo al que el usuario SÍ tiene acceso, y regresa su ruta. Se usa cuando
// el usuario intenta entrar a un módulo que no tiene (p.ej. "dashboard" no
// está en su lista) — en vez de mandarlo a una página vacía o en blanco, lo
// lleva al módulo permitido más cercano en esa secuencia. Regresa null si no
// tiene acceso a ninguno.
export async function getRutaModuloMasCercano(): Promise<string | null> {
  try {
    const modulosApi = (await import('api/modulos')).default;
    const catalogo = await modulosApi.listado();
    const ordenado = [...catalogo].sort(
      (a: any, b: any) => (a.Orden ?? 999) - (b.Orden ?? 999)
    );
    for (const m of ordenado) {
      const ruta = RUTA_POR_MODULO[m.Clave];
      if (ruta && tieneModulo(m.Clave)) return ruta;
    }
    return null;
  } catch (e) {
    console.error('Error obteniendo el módulo más cercano:', e);
    return null;
  }
}
